import { actions } from "@app/utils";
import { createApiClient } from "@app/utils/apiClient";

export const mogrtClient = createApiClient(
  actions["exd-storyteller-ai/mogrt"],
  1,
  1,
);

export const describeMogrtTemplate = async (
  path,
  type = "mogrt",
  options = {},
) => {
  if (!path) {
    throw new Error("Template path is required");
  }

  const response = await mogrtClient.post("/describe", { path, type }, options);

  if (!response.ok) {
    throw new Error("Failed to describe mogrt template", {
      cause: response.data,
    });
  }

  return response.data;
};

export const renderMogrtTemplate = async (payload = {}, options = {}) => {
  const response = await mogrtClient.post("/render", payload, options);

  if (!response.ok) {
    throw new Error("Failed to render mogrt template", {
      cause: response.data,
    });
  }

  return response.data;
};

/**
 * Render a modular template in a single request. The backend dispatches every
 * module render and returns a parent job plus the per-module render jobs.
 *
 * @param {Object} payload
 * @param {string} payload.videoId
 * @param {string} [payload.ecid]
 * @param {Array<Object>} payload.modules - Per-module render payloads, each
 *   including a `moduleName` field.
 * @param {Object} [options]
 * @returns {Promise<{ jobId: string, modularJobs: Array<{name: string, jobId: string}> }>}
 */
export const renderModularTemplate = async (payload = {}, options = {}) => {
  const response = await mogrtClient.post("/render-modular", payload, options);

  if (!response.ok) {
    throw new Error("Failed to render modular template", {
      cause: response.data,
    });
  }

  return response.data;
};

export const getVoices = async (options = {}) => {
  const response = await mogrtClient.get("/voices", {}, options);

  if (!response.ok) {
    throw new Error("Failed to fetch voices", {
      cause: response.data,
    });
  }

  return response.data;
};

export const getVoices3p = async (options = {}) => {
  const response = await mogrtClient.get("/voices-3p", {}, options);

  if (!response.ok) {
    throw new Error("Failed to fetch 3p voices", {
      cause: response.data,
    });
  }

  return response.data;
};

export const generateAudio3p = async (payload = {}, options = {}) => {
  const response = await mogrtClient.post(
    "/generate-audio-3p",
    payload,
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to generate 3p audio", {
      cause: response.data,
    });
  }

  return response.data;
};

export const generateSpeech = async (payload = {}, options = {}) => {
  // const audioPayload = {
  //   audios: [
  //     {
  //       source: {
  //         type: "text",
  //         ...payload,
  //       },
  //     },
  //   ],
  // };

  const response = await mogrtClient.post("/generate-speech", payload, options);

  if (!response.ok) {
    throw new Error("Failed to generate speech", {
      cause: response.data,
    });
  }

  return response.data;
};

export const getMogrtStatus = async (jobId, options = {}) => {
  if (!jobId) {
    throw new Error("jobId is required");
  }

  const response = await mogrtClient.post("/poll-status", { jobId }, options);

  if (!response.ok) {
    throw new Error("Failed to poll mogrt status", {
      cause: response.data,
    });
  }

  return response.data;
};

export const audioMux = async (audios = [], options = {}) => {
  const response = await mogrtClient.post("/audio-mux", { audios }, options);

  if (!response.ok) {
    throw new Error("Failed to mux audio", {
      cause: response.data,
    });
  }

  return response.data;
};

export const getAudioStatus = async (jobId, options = {}) => {
  if (!jobId) {
    throw new Error("jobId is required");
  }

  const response = await mogrtClient.post(
    "/poll-status-audio",
    { jobId },
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to poll audio status", {
      cause: response.data,
    });
  }

  return response.data;
};

export const pollMogrtStatus = async (
  jobId,
  interval = 1000,
  options = {},
  statusCallback = null,
) => {
  return new Promise((resolve, reject) => {
    const poll = setInterval(async () => {
      try {
        const statusResponse = await getMogrtStatus(jobId, options);
        const status = statusResponse?.status;

        statusCallback?.(statusResponse);

        if (statusResponse?.success && status === "succeeded") {
          clearInterval(poll);
          resolve(statusResponse);
        } else if (status === "failed") {
          clearInterval(poll);
          reject(new Error("Mogrt job failed"));
        }
      } catch (error) {
        clearInterval(poll);
        reject(error);
      }
    }, interval);
  });
};
